import * as PIXI from 'pixi.js';
import { StoryManager } from '../systems/StoryManager';
import type { DialogTree } from '../types';
import { Collider } from './Collider';
import { AssetManager } from '../managers/AssetManager';
// import {GifSprite} from "pixi.js/lib/gif/init";
import { GifSprite } from 'pixi.js/gif';
export enum NPCType {
  SMILEY = 'smiley',
  QUESTION = 'question',
}

export interface NPCConfig {
  x: number;
  y: number;
  name: string;
  dialogTree: DialogTree;
  color?: number;
  size?: number;
  scale?: number; // Multiplier for sprite, collider, and interaction radius (default: 1.0)
  interactionRadius?: number;
  colliderVisible?: boolean;
  colliderColor?: number;
  repeatable?: boolean; // If true, this NPC's dialog state won't be saved
  type?: NPCType; // Type of NPC appearance
  spriteImage?: string; // Path or asset ID for the NPC's sprite image
  flipSprite?: boolean;
  stopInteraction?: boolean;
  interactOnce?:boolean;
  noHitbox?:boolean;

  hideOnState?:string[];

}

export class NPC {
  public sprite: PIXI.Container;
  public uiContainer: PIXI.Container; // Separate container for name text and speech bubble
  private characterSprite: PIXI.Sprite | null = null;
  private graphics: PIXI.Graphics;
  private nameText: PIXI.Text;
  private interactionIndicator: PIXI.Graphics;
  public collider: Collider;

  private gifSprite:GifSprite;

  public x: number;
  public y: number;
  public name: string;
  public dialogTree: DialogTree;
  public interactionRadius: number;
  public size: number;
  private scale: number; // Scale multiplier for sprite, collider, and interaction radius
  private isPlayerNearby: boolean = false; // For interaction radius (speech bubble)
  private wasPlayerNearby: boolean = false; // Track previous frame state
  private isPlayerInCollisionRange: boolean = false; // For triggerOnCollision (uses collider-based distance)
  private wasPlayerInCollisionRange: boolean = false; // Track previous collision range state
  private isFocused: boolean = false;
  private type: NPCType;
  private stopInteraction : boolean = false;
  private onInteractionClick?: () => void;
  private hasTriggeredCollision: boolean = false; // Track if collision dialog has been triggered

  private interactOnce:boolean = false;

  private hasInteracted:boolean = false;
  private noHitbox:boolean = false;

  private hideOnState?:string[] = []

  private async initialize(config: NPCConfig) {
    this.x = config.x;
    this.y = config.y;
    this.name = config.name;
    this.dialogTree = config.dialogTree;
    this.scale = config.scale || 1.0; // Default scale multiplier
    this.size = config.size || 45; // Default size to match player (30px)
    this.type = config.type || NPCType.SMILEY; // Set smiley as default type
    this.interactOnce = config.interactOnce ? config.interactOnce : false
    // Note: interactionRadius will be set after collider creation to match actual size

    this.stopInteraction = config.stopInteraction ? config.stopInteraction : false
    this.noHitbox = config.noHitbox ? config.noHitbox : false

    this.hideOnState = config.hideOnState ? config.hideOnState : []

    const storyManager = StoryManager.getInstance();

    // Register with StoryManager if this is a repeatable NPC
    if (config.repeatable) {
      storyManager.addRepeatableNPC(this.name);
    }

    let currentStates = storyManager.getState()

    // Create main container for NPC sprite
    this.sprite = new PIXI.Container();
    this.sprite.x = this.x;
    this.sprite.y = this.y;
    this.sprite.zIndex = 1; // NPCs should be below the player (player has zIndex 2)
    this.sprite.sortableChildren = true; // Enable z-index sorting for children

    // Create separate UI container for name text and speech bubble (will be added to camera at high z-index)
    this.uiContainer = new PIXI.Container();
    this.uiContainer.x = this.x;
    this.uiContainer.y = this.y;
    this.uiContainer.zIndex = 100; // UI elements should be above all world elements

    // Create either a sprite image or fallback to a graphics object

    let shouldHideBecauseOfState = false

    if(config.hideOnState){
      for(let state of config.hideOnState){
        if(storyManager.checkStateExists(state)){
          shouldHideBecauseOfState = true
        }
      }
    }

    if (config.spriteImage) {
      // Use AssetManager to get the texture
      const assetManager = AssetManager.getInstance();

      // Try to get texture from asset manager first
      let texture: PIXI.Texture | undefined;
      const assetId = config.spriteImage.startsWith('/')
          ? config.spriteImage.split('/').pop()?.replace('.png', '')
          : config.spriteImage;

      if (assetId?.startsWith('npc_')) {
        texture = assetManager.getImageTexture(assetId);
      }

      // If not available, load directly
      if (!texture) {
        texture = PIXI.Texture.from(config.spriteImage);
      }

      this.characterSprite = new PIXI.Sprite(texture);
      this.characterSprite.anchor.set(0.5);
      this.characterSprite.zIndex = 0; // Character sprite at base level

      // Scale the sprite to make it proportional to the player size
      // Use a fixed scale factor to ensure NPCs are appropriately sized
      // A constant multiplier of 2.0 makes NPCs roughly the same visual size as the player
      // Apply the scale multiplier for consistent sizing with collider and interaction radius
      const baseScale = this.size / Math.max(this.characterSprite.width, this.characterSprite.height);

      // console.log("BASESCALE", baseScale, this.scale)
      this.characterSprite.scale.set(baseScale * 2.0 * this.scale, baseScale * 2.0 * this.scale); //Sementara di komen karena kayanya ini ngubah scale

      if(config.flipSprite){
        this.characterSprite.scale._x = -1 * this.characterSprite.scale._x
      }

      // Create an invisible graphics object for compatibility
      this.graphics = new PIXI.Graphics();
      this.graphics.zIndex = 0; // Graphics at base level

      // Add sprite to the container
      this.sprite.addChild(this.characterSprite);



      if(config.spriteImage?.includes(".gif")){
        await this.loadGifSprite(config.spriteImage)
      }

      if(config.hideOnState){
        for(let state of config.hideOnState){
          if(storyManager.checkStateExists(state)){
            this.sprite.scale.set(0,0)
            this.stopInteraction = true
          }
        }
      }

    } else {
      // Fallback to graphics for backward compatibility
      this.graphics = new PIXI.Graphics();
      this.graphics.zIndex = 0; // Graphics at base level
      // Apply scale to fallback graphics size
      const scaledSize = this.size * this.scale;
      this.graphics.beginFill(config.color || 0xe74c3c); // Red color by default
      this.graphics.drawRoundedRect(-scaledSize / 2, -scaledSize / 2, scaledSize, scaledSize, 3);
      this.graphics.endFill();

      // Draw face based on type (fallback) - scale the features proportionally
      const faceScale = this.scale;
      if (this.type === NPCType.QUESTION) {
        // Question mark face
        this.graphics.lineStyle(2 * faceScale, 0xffffff);
        this.graphics.arc(0, -6 * faceScale, 5 * faceScale, -0.5 * Math.PI, 0.5 * Math.PI, false);
        this.graphics.lineTo(0, 0);
        this.graphics.lineStyle(0);

        // Draw the dot at the bottom
        this.graphics.beginFill(0xffffff);
        this.graphics.drawCircle(0, 5 * faceScale, 2 * faceScale);
        this.graphics.endFill();
      } else {
        // Default smiley face
        this.graphics.beginFill(0xffffff);
        this.graphics.drawCircle(-6 * faceScale, -6 * faceScale, 2 * faceScale); // Left eye
        this.graphics.drawCircle(6 * faceScale, -6 * faceScale, 2 * faceScale); // Right eye
        this.graphics.endFill();

        // Mouth (smile)
        this.graphics.lineStyle(1 * faceScale, 0xffffff);
        this.graphics.arc(0, 2 * faceScale, 6 * faceScale, 0, Math.PI);
      }

      // Add graphics to container
      this.sprite.addChild(this.graphics);
    }

    // Create collider for the NPC, matching the actual sprite dimensions
    let colliderWidth = this.size * this.scale; // Apply scale to base size
    let colliderHeight = this.size * this.scale;

    // If using a sprite image, calculate the actual displayed dimensions
    if (this.characterSprite) {
      // Calculate the actual displayed dimensions using texture size and scale
      // (scale is already applied to characterSprite.scale)
      colliderWidth = this.characterSprite.texture.width * Math.abs(this.characterSprite.scale.x);
      colliderHeight = this.characterSprite.texture.height * Math.abs(this.characterSprite.scale.y);

      // if(config.spriteImage?.includes(".gif")){
      //   colliderWidth = this.gifSprite.width
      //   colliderHeight = this.gifSprite.height
      // }

    }

    if(this.noHitbox || shouldHideBecauseOfState){
      this.collider = new Collider({
        x: this.x - colliderWidth / 2,  // World coordinates for collision bounds
        y: this.y - colliderHeight / 2, // World coordinates for collision bounds
        width: 0,
        height: 0,
        color: config.colliderColor || 0xff6b6b,
        visible: config.colliderVisible ?? false, // Hidden by default
      });
    }else{
      this.collider = new Collider({
        x: this.x - colliderWidth / 2,  // World coordinates for collision bounds
        y: this.y - colliderHeight / 2, // World coordinates for collision bounds
        width: colliderWidth,
        height: colliderHeight,
        color: config.colliderColor || 0xff6b6b,
        visible: config.colliderVisible ?? false, // Hidden by default
      });
    }




    // Adjust collider sprite position to be relative to NPC container
    // Since collider is added as child of this.sprite (which is at this.x, this.y),
    // we need to offset the sprite position
    this.collider.sprite.x = -colliderWidth / 2;
    this.collider.sprite.y = -colliderHeight / 2;

    // Set interaction radius based on actual collider size for consistent triggerOnCollision
    // Use the larger dimension (width or height) plus a padding
    const maxColliderDimension = Math.max(colliderWidth, colliderHeight);
    const interactionPadding = config.interactionRadius !== undefined
        ? config.interactionRadius // Use custom value if provided
        : 30; // Default padding around collider
    this.interactionRadius = maxColliderDimension / 2 + interactionPadding;

    // Create name text - positioned above the sprite
    this.nameText = new PIXI.Text(this.name, {
      fontFamily: 'Rosarivo, serif',
      fontSize: 16,
      fill: 0xffffff,
      align: 'center',
    });
    this.nameText.anchor.set(0.5, 1);
    this.nameText.y = -(this.size * this.scale) / 2 - 30; // Position above the scaled sprite
    this.nameText.resolution = window.devicePixelRatio || 2;
    this.nameText.style.padding = 4;
    this.nameText.zIndex = 10; // Name text should always be on top

    // Create interaction indicator (appears when player is nearby)
    this.interactionIndicator = new PIXI.Graphics();
    this.createInteractionIndicator();
    this.interactionIndicator.visible = false;
    this.interactionIndicator.zIndex = 10; // Speech bubble should always be on top

    // Make the interaction indicator interactive and clickable
    this.interactionIndicator.eventMode = 'static';
    this.interactionIndicator.cursor = 'pointer';
    this.interactionIndicator.on('pointerdown', () => {
      if (this.onInteractionClick) {
        this.onInteractionClick();
      }
    });

    // Add UI elements to the separate UI container (will be rendered at high z-index)
    this.uiContainer.addChild(this.nameText);
    this.uiContainer.addChild(this.interactionIndicator);

    // Add collider to the main sprite container
    this.sprite.addChild(this.collider.sprite);
  }

  constructor(config: NPCConfig) {
    this.initialize(config)
  }

  private async loadGifSprite(path:string): Promise<void>{
    const gifSource = await PIXI.Assets.load(path);

    // Create GifSprite with loaded source using proper constructor
    const gifSprite = new GifSprite(gifSource);
    this.gifSprite = gifSprite
    this.characterSprite = gifSprite

    // Set up sprite properties
    gifSprite.anchor.set(0.5);
    gifSprite.loop = true;
    gifSprite.animationSpeed = 1;
    gifSprite.zIndex = 0; // GIF sprite at base level, below name text and speech bubble
    
    // Apply scale to GIF sprite for consistency with other sprites
    const baseScale = this.size / Math.max(gifSprite.width, gifSprite.height);
    gifSprite.scale.set(baseScale * 2.0 * this.scale);

    gifSprite.play();

    this.sprite.addChild(gifSprite);
  }

  private createInteractionIndicator(): void {

    if(this.stopInteraction){
      return;
    }

    this.interactionIndicator.clear();

    // Create a speech bubble shape
    const bubbleWidth = 24;
    const bubbleHeight = 18;
    const tailSize = 4;
    const scaledHalfSize = (this.size * this.scale) / 2; // Use scaled size for positioning

    // Main bubble body (rounded rectangle)
    this.interactionIndicator.beginFill(0xffffff, 0.95); // White with slight transparency
    this.interactionIndicator.lineStyle(1, 0x000000, 0.3); // Subtle dark border
    this.interactionIndicator.drawRoundedRect(
      -bubbleWidth / 2,
      -scaledHalfSize - bubbleHeight - 15,
      bubbleWidth,
      bubbleHeight,
      6,
    );
    this.interactionIndicator.endFill();

    // Speech bubble tail (small triangle pointing down)
    this.interactionIndicator.beginFill(0xffffff, 0.95);
    this.interactionIndicator.lineStyle(1, 0x000000, 0.3);
    this.interactionIndicator.moveTo(-tailSize, -scaledHalfSize - 15);
    this.interactionIndicator.lineTo(0, -scaledHalfSize - 8);
    this.interactionIndicator.lineTo(tailSize, -scaledHalfSize - 15);
    this.interactionIndicator.closePath();
    this.interactionIndicator.endFill();

    // Add chat dots inside the bubble (three dots like a typing indicator)
    const dotRadius = 1.5;
    const dotSpacing = 4;
    const dotY = -scaledHalfSize - bubbleHeight / 2 - 15;

    this.interactionIndicator.beginFill(0x666666); // Gray dots
    this.interactionIndicator.drawCircle(-dotSpacing, dotY, dotRadius);
    this.interactionIndicator.drawCircle(0, dotY, dotRadius);
    this.interactionIndicator.drawCircle(dotSpacing, dotY, dotRadius);
    this.interactionIndicator.endFill();
  }

  public update(
    _deltaTime: number, 
    playerPosition: { x: number; y: number },
    playerCollider?: Collider
  ): void {
    // Calculate distance to player
    const dx = playerPosition.x - this.x;
    const dy = playerPosition.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Store previous states before updating
    this.wasPlayerNearby = this.isPlayerNearby;
    this.wasPlayerInCollisionRange = this.isPlayerInCollisionRange;
    
    // Update proximity state (for regular interaction/speech bubble)
    this.isPlayerNearby = distance <= this.interactionRadius * 1.2;
    
    // Update collision range state (for triggerOnCollision)
    // Account for both NPC and player collider sizes for accurate collision detection
    const npcBounds = this.collider.getBounds();
    const npcRadius = Math.max(npcBounds.width, npcBounds.height) / 2;
    
    let collisionThreshold = npcRadius;
    if (playerCollider) {
      // Add player's radius for true collision detection (when edges touch)
      const playerBounds = playerCollider.getBounds();
      const playerRadius = Math.max(playerBounds.width, playerBounds.height) / 2;
      collisionThreshold = this.interactionRadius + playerRadius;
    }
    
    this.isPlayerInCollisionRange = distance <= collisionThreshold;

    // Only show interaction indicator if both nearby AND focused
    const shouldShowIndicator = this.isPlayerNearby && this.isFocused;

    if (this.interactionIndicator.visible !== shouldShowIndicator) {
      this.interactionIndicator.visible = shouldShowIndicator;
    }

    // Add subtle floating animation to interaction indicator
    if (shouldShowIndicator) {
      const time = Date.now() * 0.004;
      // Gentle floating motion (up and down)
      const floatOffset = Math.sin(time) * 2;
      this.interactionIndicator.y = floatOffset;

      // Subtle breathing scale effect
      const breatheScale = 1 + Math.sin(time * 1.2) * 0.05;
      this.interactionIndicator.scale.set(breatheScale);
    }
  }

  public setFocused(focused: boolean): void {
    this.isFocused = focused;
  }

  public isNearPlayer(): boolean {
    return this.isPlayerNearby;
  }

  public getFocused(): boolean {
    return this.isFocused;
  }

  public canInteract(playerPosition: { x: number; y: number }): boolean {

    if(this.interactOnce && this.hasInteracted){
      return false
    }

    if(this.hideOnState){

      const storyManager = StoryManager.getInstance();

      for(let state of this.hideOnState){
        if(storyManager.checkStateExists(state)){
          this.sprite.scale.set(0,0)
          this.nameText.text = ""
          this.stopInteraction = true
          return false;
        }
      }
    }


    const dx = playerPosition.x - this.x;
    const dy = playerPosition.y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= this.interactionRadius * 1.2;
  }

  public interact():
    | DialogTree
    | {
        type: 'repeat';
        speaker: string;
        text: string;
        characterImage?: string;
        backgroundImage?: string;
        isEnd?: boolean;
      } {

    console.log("DBXXXXX interact is called?")

    if(this.stopInteraction){
      return
    }

    if(this.interactOnce && this.hasInteracted){
      return
    }

    this.hasInteracted = true

    if(this.interactOnce){
      this.characterSprite?.scale.set(0,0)
    }

    const storyManager = StoryManager.getInstance();

    // Check if this NPC has been interacted with before
    if (storyManager.hasInteractedWithNPC(this.name)) {
      const interactionState = storyManager.getNPCInteractionState(this.name);
      if (interactionState?.lastSpeaker && interactionState.lastText) {
        return {
          type: 'repeat',
          speaker: interactionState.lastSpeaker,
          text: interactionState.lastText,
          characterImage: interactionState.lastCharacterImage,
          backgroundImage: interactionState.lastBackgroundImage,
          isEnd: interactionState.lastIsEnd,
        };
      }
    }

    // First interaction - return the full dialog tree
    return this.dialogTree;
  }

  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  public getBounds(): PIXI.Rectangle {
    return new PIXI.Rectangle(this.x - this.size / 2, this.y - this.size / 2, this.size, this.size);
  }

  /**
   * Get the NPC's collider for collision detection
   */
  public getCollider(): Collider {
    return this.collider;
  }

  /**
   * Check if a point collides with this NPC
   */
  public containsPoint(x: number, y: number): boolean {
    return this.collider.containsPoint(x, y);
  }

  /**
   * Check if a rectangle intersects with this NPC's collider
   */
  public intersects(rect: PIXI.Rectangle): boolean {
    return this.collider.intersects(rect);
  }

  /**
   * Check if another collider intersects with this NPC
   */
  public intersectsCollider(other: Collider): boolean {
    return this.collider.intersectsCollider(other);
  }

  /**
   * Set the visibility of the NPC's collider (useful for debugging)
   */
  public setColliderVisible(visible: boolean): void {
    this.collider.setVisible(visible);
  }

  /**
   * Update the NPC's position (this would be used if NPCs move in the future)
   */
  public setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
    this.sprite.x = x;
    this.sprite.y = y;
    this.uiContainer.x = x;
    this.uiContainer.y = y;

    // Update collider position to match NPC position
    const colliderBounds = this.collider.getBounds();
    this.collider.setPosition(x - colliderBounds.width / 2, y - colliderBounds.height / 2);
  }

  /**
   * Set the callback function to be called when the speech bubble is clicked
   */
  public setOnInteractionClick(callback: () => void): void {
    this.onInteractionClick = callback;
  }

  /**
   * Check if this NPC should trigger dialog on collision
   * Returns true only when player's collider just started colliding with NPC's collider
   */
  public shouldTriggerOnCollision(): boolean {
    // Trigger only if: enabled, not triggered before, player just entered collision range
    return this.dialogTree.triggerOnCollision === true
        && this.isPlayerInCollisionRange
        // && !this.hasTriggeredCollision
        // && !this.wasPlayerInCollisionRange;
  }

  /**
   * Mark that collision dialog has been triggered
   */
  public markCollisionTriggered(): void {
    this.hasTriggeredCollision = true;
  }

  /**
   * Reset collision trigger state (useful for repeatable NPCs)
   */
  public resetCollisionTrigger(): void {
    this.hasTriggeredCollision = false;
  }
}
